
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import HeaderTitle from '@/components/HeaderTitle.vue';
import Installing from '@/components/Installing.vue';
import { StoreModel } from '@/models/store';
import { BillingPackageModel } from '@/models/billing_package';
import { UserPackageModel } from '@/models/user_package';
import { BillingPackageList } from '@/collections/billing_packages';
import { isShopifyEmbedded } from '@shopify/app-bridge/utilities';
import { Redirect, History } from '@shopify/app-bridge/actions';
import { EventHandler } from '@/modules/events';
import { EventBus } from '@/main';
import FeatureComparisonTable from '@/components/FeatureComparisonTable.vue';
import DiscountBadge from '@/components/pricing/DiscountBadge.vue';
import StickyBottomBanner from '@/components/pricing/StickyBottomBanner.vue';
import ROICalculator from '@/components/pricing/ROICalculator.vue';
import SocialProof from '@/components/pricing/SocialProof.vue';
import FreePlanInterceptionDialog from '@/components/pricing/FreePlanInterceptionDialog.vue';
import {
  getRemainingTime,
  isTimerExpired,
  formatCountdown,
  getColorCode
} from '@/utils/discountTimer';
import {
  getFirstMonthDiscountForPlan
} from '@/services/discountService';
import { PricingPageEventHandler } from '@/modules/pricingPageEvents';

// Add interface for feature description
interface FeatureDescription {
  text: string;
  index: number;
}

@Component({
  components: {
    HeaderTitle,
    Installing,
    FeatureComparisonTable,
    DiscountBadge,
    StickyBottomBanner,
    ROICalculator,
    SocialProof,
    FreePlanInterceptionDialog,
  },
})
export default class OnboardPricing extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  @Prop({ default: () => [] })
  public timeRangeLimit!: string[];
  @Prop({ default: false })
  public isAthenaFinished!: boolean;
  @Prop({ default: null })
  public app!: any;
  public success: boolean = false;
  public error: boolean = false;
  public loading: boolean = false;
  public billingPackages: BillingPackageList = new BillingPackageList();
  public filteredBillingPackages: BillingPackageList = new BillingPackageList();
  public newUserPackage: UserPackageModel = new UserPackageModel();
  public freeBillingPackage: BillingPackageModel = new BillingPackageModel();
  public paidBillingPackage: BillingPackageModel = new BillingPackageModel();
  public paidBillingPackageList: BillingPackageList = new BillingPackageList();
  public backupUserPackage: UserPackageModel = new UserPackageModel();
  public isYearly: boolean = false;
  public submitting: boolean = false;
  public maxFeatureLines: number = 7;
  public viewAllFeatures: boolean = false;
  public currentDiscount: any = null;

  // Timer-related data properties (Task 4.1)
  public isOfferExpired: boolean = false;      // Whether 30-minute offer has expired
  public countdownText: string = '';           // Formatted countdown text (e.g., "23m 45s")
  public remainingMs: number = 0;              // Remaining milliseconds for timer
  public countdownInterval: any = null;        // Interval reference for countdown updates

  public offerMessage: string = 'Limited Time: Get up to 60% OFF your first month of Replenishment Center!';

  // Free plan interception dialog visibility
  public showInterceptionDialog: boolean = false;

  // Analytics tracker for pricing page events
  private pricingTracker: PricingPageEventHandler | null = null;
  public async created() {
    localStorage.setItem('onboard_page', '/onboard-pricing');
    this.loading = true;

    // Initialize pricing analytics tracker
    this.pricingTracker = new PricingPageEventHandler();

    try {
      await this.billingPackages.fetch();
      await this.getBillingPackage();
      this.error = false;
      this.success = true;
    } catch (e: any) {
      this.error = true;
      this.success = false;
    }
    this.loading = false;

    // Initialize countdown timer using store.installedAt
    this.initializeCountdownTimer();

    // Track pricing page view after data is loaded (non-blocking)
    try {
      if (this.pricingTracker && !this.loading && !this.error) {
        this.pricingTracker.trackPageViewed({
          referrer: document.referrer || 'direct',
          hasActiveDiscount: !!this.currentDiscount,
          discountCode: this.currentDiscount?.code,
        });
      }
    } catch (trackingError) {
      // Silently fail - don't let analytics break the page
      console.warn('[OnboardPricing] Analytics tracking failed:', trackingError);
    }
  }

  /**
   * Watch for billing toggle changes (monthly/yearly)
   * Tracks user behavior when comparing pricing options
   * DISABLED: Toggle hidden for new users (Story SUB.3)
   */
  // @Watch('isYearly')
  // public onBillingToggleChange(newValue: boolean): void {
  //   // Track billing toggle (non-blocking, fail silently)
  //   try {
  //     if (this.pricingTracker) {
  //       const packagesOnScreen = this.filteredBillingPackages.items.length;
  //       this.pricingTracker.trackBillingToggle(
  //         newValue ? 'yearly' : 'monthly',
  //         packagesOnScreen
  //       );
  //     }
  //   } catch (trackingError) {
  //     // Silently fail - don't let analytics break functionality
  //     console.warn('[OnboardPricing] Billing toggle tracking failed:', trackingError);
  //   }
  // }

  /**
   * Watch for discount banner visibility
   * Tracks when discount becomes available and banner is shown
   */
  @Watch('currentDiscount')
  public onDiscountLoaded(newDiscount: any): void {
    // Track discount banner viewed when discount is loaded (non-blocking)
    try {
      if (this.pricingTracker && newDiscount && !this.isOfferExpired) {
        this.pricingTracker.trackDiscountBannerViewed({
          discountCode: newDiscount.code,
          discountType: newDiscount.type,
          discountValue: newDiscount.rate || newDiscount.discountAmount || 0,
          appliesTo: newDiscount.billingPackageCode || 'all',
          isFirstMonthOnly: true,
          validUntil: this.store?.installedAt
            ? new Date(new Date(this.store.installedAt as any).getTime() + 30 * 60 * 1000)
            : undefined,
        });
      }
    } catch (trackingError) {
      // Silently fail - don't let analytics break page load
      console.warn('[OnboardPricing] Banner view tracking failed:', trackingError);
    }
  }

  /**
   * Task 4.2: Initialize countdown timer
   * Uses StoreModel.installedAt for cross-device consistency
   */
  public initializeCountdownTimer(): void {
    // Check if store.installedAt exists
    if (!this.store || !this.store.installedAt) {
      console.warn('[OnboardPricing] Store installedAt not available, timer disabled');
      this.isOfferExpired = true;
      return;
    }

    // Calculate initial timer state
    this.updateTimerState();

    // Set up 1-second interval to update countdown
    this.countdownInterval = setInterval(() => {
      this.updateTimerState();

      // Clear interval when expired
      if (this.isOfferExpired && this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    }, 1000);
  }

  /**
   * Update timer state based on store.installedAt
   */
  private updateTimerState(): void {
    if (!this.store || !this.store.installedAt) {
      this.isOfferExpired = true;
      this.countdownText = '';
      this.remainingMs = 0;
      return;
    }

    // Calculate remaining time from installedAt
    this.remainingMs = getRemainingTime(this.store.installedAt as any);
    this.isOfferExpired = isTimerExpired(this.store.installedAt as any);

    // Format countdown text
    if (!this.isOfferExpired) {
      this.countdownText = formatCountdown(this.remainingMs);
    } else {
      this.countdownText = '';
    }
  }

  /**
   * Cleanup timer interval and track session abandonment on component destruction
   */
  public beforeDestroy(): void {
    // Track session abandonment if user didn't complete subscription (non-blocking)
    try {
      if (this.pricingTracker && !this.submitting) {
        this.pricingTracker.trackSessionAbandoned('navigation');
      }
    } catch (trackingError) {
      // Silently fail - never block navigation
      console.warn('[OnboardPricing] Session abandonment tracking failed:', trackingError);
    }

    // Cleanup countdown timer
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  // Helper method to get filtered features for a package
  public getFilteredFeatures(packageItem: BillingPackageModel): FeatureDescription[] {
    if (!packageItem || !packageItem.description) { return []; }

    const features = packageItem.description.split('\n')
      .map((text, index) => ({ text, index }))
      .filter((item) => item.text !== '' && item.index > 0);

    if (!this.viewAllFeatures) {
      return features.filter((item) => item.index < this.maxFeatureLines);
    }

    return features;
  }

  public calculateDisplayPrice(billingPackage: BillingPackageModel) {
    let price: number = billingPackage.price;

    // Apply first-month discount for MONTHLY billing only
    if (!this.isYearly && this.currentDiscount?.billingPackageCode && !this.isOfferExpired) {
      const packageCode = (billingPackage as any).code;
      const packageMatches = packageCode === this.currentDiscount.billingPackageCode;
      if (packageMatches && this.currentDiscount.type === 'PERCENTAGE_DISCOUNT') {
        const discountAmount = price * (this.currentDiscount.rate / 100);
        price = price - discountAmount;
      } else if (packageMatches && this.currentDiscount.type === 'FIXED_DISCOUNT_AMOUNT') {
        price = price - this.currentDiscount.discountAmount;
      }
    }

    // Yearly pricing: 10 months for 12 months price (2 months free)
    if (this.isYearly) {
      price = (price * 10) / 12;
    }

    return price.toFixed(2);
  }

  public async getBillingPackage() {
    this.paidBillingPackageList = new BillingPackageList();
    this.filteredBillingPackages = new BillingPackageList();
    let discount: any;

    // Fetch first-month discount for current store's plan
    try {
      if (this.store && this.store.assistyPlan) {
        discount = await getFirstMonthDiscountForPlan(this.store.assistyPlan);
        console.log('[OnboardPricing] Store.assistyPlan:', this.store.assistyPlan);
        console.log('[OnboardPricing] Discount fetched:', discount);
        if (discount) {
          this.currentDiscount = discount;
          this.offerMessage = this.currentDiscount.description;
          console.log('[OnboardPricing] First-month discount loaded:', {
            code: discount.code,
            plan: this.store.assistyPlan,
            billingPackageCode: discount.billingPackageCode,
            rate: discount.rate
          });
        } else {
          this.currentDiscount = null;
          console.log('[OnboardPricing] No first-month discount available for plan:', this.store.assistyPlan);
        }
      } else {
        this.currentDiscount = null;
        console.warn('[OnboardPricing] Store or assistyPlan not available');
      }
    } catch (e) {
      console.error('[OnboardPricing] Discount fetch failed:', e);
      this.currentDiscount = null;
    }
    
    const assistyPlan = this.store.assistyPlan;
    for (const item of this.billingPackages.items) {
      if (item.price === 0) {
        this.freeBillingPackage = item;
      } else if (assistyPlan === item.shopifyPlanName) {
        this.paidBillingPackageList.add(item);
      }
    }
    
    // If no packages were found with matching shopifyPlanName, include all paid packages
    if (this.paidBillingPackageList.items.length === 0) {
      for (const item of this.billingPackages.items) {
        if (item.price > 0) {
          this.paidBillingPackageList.add(item);
        }
      }
    }
    
    // sort the paid billing packages by price in descending order
    this.paidBillingPackageList.items.sort((a, b) => b.price - a.price);
    
    this.filteredBillingPackages = new BillingPackageList();

    for (const item of this.paidBillingPackageList.items) {
      this.filteredBillingPackages.add(item);
    }
  }

  public getTranslatedLabel(module: string, text: string) {
    if (this.$te(`${module}.${text}`)) {
      return this.$t(`${module}.${text}`);
    } else {
      return text;
    }
  }

  public isMaxFeature(index: number) {
    if (this.viewAllFeatures) {
      return false;
    }
    if (index < this.maxFeatureLines) {
      return false;
    }
    return true;
  }

  public async submitPackage(
    item: BillingPackageModel,
    trialDays: number = 14,
  ) {
    this.submitting = true;
    this.newUserPackage.billingPackageId = item.id;

    // Track subscribe button click (non-blocking, fail silently)
    try {
      if (this.pricingTracker) {
        const packageData = {
          packageCode: (item as any).code || '',
          packageName: item.name,
          price: item.price,
          isYearly: this.isYearly,
        };

        const discountData = this.currentDiscount && !this.isOfferExpired && !this.isYearly
          ? {
              discountCode: this.currentDiscount.code,
              discountType: this.currentDiscount.type,
              discountValue: this.currentDiscount.rate || this.currentDiscount.discountAmount || 0,
              appliesTo: this.currentDiscount.billingPackageCode || 'all',
              isFirstMonthOnly: true,
            }
          : undefined;

        this.pricingTracker.trackSubscribeButtonClicked(packageData, discountData);
      }
    } catch (trackingError) {
      // Silently fail - never let analytics break subscription flow
      console.warn('[OnboardPricing] Subscribe tracking failed:', trackingError);
    }

    // Apply first-month discount code if all conditions met
    console.log('[OnboardPricing] Discount application check:', {
      hasDiscount: !!this.currentDiscount,
      hasBillingPackageCode: !!this.currentDiscount?.billingPackageCode,
      discountBillingPackageCode: this.currentDiscount?.billingPackageCode,
      discountCode: this.currentDiscount?.code,
      isYearly: this.isYearly,
      isOfferExpired: this.isOfferExpired,
      packageCode: (item as any).code,
      packageName: item.name,
      packageId: item.id
    });

    if (this.currentDiscount?.billingPackageCode && !this.isYearly && !this.isOfferExpired) {
      const packageCode = (item as any).code;
      const packageMatches = packageCode === this.currentDiscount.billingPackageCode;

      console.log('[OnboardPricing] Package match check:', {
        packageCode,
        discountBillingPackageCode: this.currentDiscount.billingPackageCode,
        packageMatches,
        willApplyDiscount: packageMatches
      });

      if (packageMatches) {
        this.newUserPackage.discountCode = this.currentDiscount.code;
        console.log('[OnboardPricing] ✅ Discount code APPLIED:', this.currentDiscount.code);
      } else {
        console.warn('[OnboardPricing] ❌ Discount NOT applied - package code mismatch:', {
          expected: this.currentDiscount.billingPackageCode,
          actual: packageCode
        });
      }
    } else {
      console.warn('[OnboardPricing] ❌ Discount NOT applied - conditions not met:', {
        reason: !this.currentDiscount ? 'No discount loaded' :
                !this.currentDiscount.billingPackageCode ? 'No billingPackageCode in discount' :
                this.isYearly ? 'Yearly billing selected' :
                this.isOfferExpired ? 'Offer expired' : 'Unknown'
      });
    }
    
    let returnUrl = `${process.env.VUE_APP_SUBSCRIPTION_RETURN_URL}`;
    if (isShopifyEmbedded()) {
      returnUrl = `https://admin.shopify.com/store/${this.store.shopName.split('.')[0]}/apps/${process.env.VUE_APP_NAME}/subscription/confirm`;
    }
    const confirmationUrl = await this.newUserPackage.create(
      trialDays,
      0,
      returnUrl,
      [],
      this.isYearly
    );

    if (this.newUserPackage.status === 'CONFIRMING' && confirmationUrl) {
      try {
        const eventHandler = new EventHandler({
          store: this.store,
        });
        await eventHandler.track('ONBOARDING_CONFIRMING_PAID_PACKAGE', {
          billing_package_id: this.newUserPackage.billingPackageId,
        });
        await this.store.sendEvent('ONBOARDING_CONFIRMING_PAID_PACKAGE', {
          billing_package_id: this.newUserPackage.billingPackageId,
        });
      } catch (e) {
        // ignore this
      }

      // Track subscription creation in analytics (non-blocking)
      try {
        if (this.pricingTracker) {
          const finalPrice = parseFloat(this.calculateDisplayPrice(item));
          this.pricingTracker.trackSubscriptionCreated({
            packageCode: (item as any).code || '',
            packageName: item.name,
            price: item.price,
            isYearly: this.isYearly,
            discountApplied: !!this.newUserPackage.discountCode,
            discountCode: this.newUserPackage.discountCode,
            finalPrice: finalPrice,
            subscriptionId: this.newUserPackage.id?.toString() || 'pending',
          });
        }
      } catch (trackingError) {
        // Silently fail - never block subscription flow
        console.warn('[OnboardPricing] Subscription creation tracking failed:', trackingError);
      }

      // Track payment redirect to Shopify (non-blocking)
      try {
        if (this.pricingTracker && confirmationUrl) {
          const finalPrice = parseFloat(this.calculateDisplayPrice(item));
          this.pricingTracker.trackPaymentRedirect({
            packageCode: (item as any).code || '',
            packageName: item.name,
            price: item.price,
            isYearly: this.isYearly,
            discountApplied: !!this.newUserPackage.discountCode,
            discountCode: this.newUserPackage.discountCode,
            finalPrice: finalPrice,
            subscriptionId: this.newUserPackage.id?.toString() || 'pending',
          }, confirmationUrl);
        }
      } catch (trackingError) {
        // Silently fail - never block redirect to Shopify
        console.warn('[OnboardPricing] Payment redirect tracking failed:', trackingError);
      }

      if (isShopifyEmbedded()) {
        Redirect.create(this.app).dispatch(
          Redirect.Action.REMOTE,
          confirmationUrl,
        );
      } else {
        window.location.assign(confirmationUrl);
      }
    } else {
      await this.store.updateInstallationStatus();
      try {
        const eventHandler = new EventHandler({
          store: this.store,
        });
        await eventHandler.track('ONBOARDING_START_FREE_PACKAGE', {
          billing_package_id: this.newUserPackage.billingPackageId,
        });
        await this.store.sendEvent('ONBOARDING_START_FREE_PACKAGE', {
          billing_package_id: this.newUserPackage.billingPackageId,
        });
      } catch (e) {
        // ignore this
      }

      // Track free package subscription in analytics (non-blocking)
      try {
        if (this.pricingTracker) {
          this.pricingTracker.trackSubscriptionCreated({
            packageCode: (item as any).code || '',
            packageName: item.name,
            price: item.price,
            isYearly: this.isYearly,
            discountApplied: false,
            finalPrice: 0,
            subscriptionId: this.newUserPackage.id?.toString() || 'free',
          });
        }
      } catch (trackingError) {
        // Silently fail - never block subscription flow
        console.warn('[OnboardPricing] Free package tracking failed:', trackingError);
      }

      EventBus.$emit('check-athena-finish-interval');
      this.$router.push('/subscription');
    }
  }

  /**
   * Check if a package is the premium plan (highest-priced)
   *
   * Identifies the most expensive package to display "BEST VALUE" badge
   * (when discount badges are not active). Used for UI highlighting.
   *
   * @param {BillingPackageModel} item - Package to check
   * @returns {boolean} True if this is the highest-priced package
   *
   * @remarks
   * - Compares against all packages in filteredBillingPackages
   * - Only returns true for paid packages (price > 0)
   * - If multiple packages have same highest price, all return true
   * - Used in conjunction with !shouldShowDiscountBadge to prevent badge conflicts
   *
   * @example
   * ```vue
   * <div v-if="isPremiumPlan(item) && !shouldShowDiscountBadge(item)">
   *   BEST VALUE
   * </div>
   * ```
   *
   * @see shouldShowDiscountBadge - Prevents badge overlap with discount badges
   */
  public isPremiumPlan(item: BillingPackageModel): boolean {
    // Find the highest priced package in the list
    const highestPrice = Math.max(...this.filteredBillingPackages.items.map(pkg => pkg.price));
    // Return true if this item has the highest price
    return item.price === highestPrice && highestPrice > 0;
  }

  public async submitFreePackage() {
    // Show interception dialog instead of directly submitting
    this.showFreePlanInterceptionDialog();
  }

  /**
   * Show free plan interception dialog
   *
   * Displays modal to encourage user to try paid trial instead of free plan.
   * Tracks analytics event when dialog is shown.
   */
  public showFreePlanInterceptionDialog(): void {
    this.showInterceptionDialog = true;

    // Track interception shown (non-blocking)
    try {
      if (this.pricingTracker) {
        this.pricingTracker.trackFreePlanInterceptionShown();
      }
    } catch (trackingError) {
      console.warn('[OnboardPricing] Free plan interception tracking failed:', trackingError);
    }
  }

  /**
   * Handle user choosing trial from interception dialog
   *
   * Closes dialog and submits premium package with 14-day trial.
   */
  public async handleInterceptionChooseTrial(): Promise<void> {
    this.showInterceptionDialog = false;

    // Track conversion from free to trial (non-blocking)
    try {
      if (this.pricingTracker) {
        this.pricingTracker.trackFreePlanInterceptionConverted('trial');
      }
    } catch (trackingError) {
      console.warn('[OnboardPricing] Trial conversion tracking failed:', trackingError);
    }

    // Submit premium package with trial
    await this.submitPremiumPackage();
  }

  /**
   * Handle user insisting on free plan from interception dialog
   *
   * Closes dialog and proceeds with free package creation.
   */
  public async handleInterceptionChooseFree(): Promise<void> {
    this.showInterceptionDialog = false;

    // Track user chose free after seeing trial offer (non-blocking)
    try {
      if (this.pricingTracker) {
        this.pricingTracker.trackFreePlanInterceptionConverted('free');
      }
    } catch (trackingError) {
      console.warn('[OnboardPricing] Free plan conversion tracking failed:', trackingError);
    }

    // Submit free package directly
    if (this.freeBillingPackage && this.freeBillingPackage.id) {
      await this.submitPackage(this.freeBillingPackage, 0);
    }
  }

  /**
   * Handle user dismissing interception dialog
   *
   * Tracks dismissal event and closes dialog without action.
   */
  public handleInterceptionDismiss(): void {
    this.showInterceptionDialog = false;

    // Track dialog dismissed (non-blocking)
    try {
      if (this.pricingTracker) {
        this.pricingTracker.trackFreePlanInterceptionDismissed();
      }
    } catch (trackingError) {
      console.warn('[OnboardPricing] Interception dismissal tracking failed:', trackingError);
    }
  }

  /**
   * Submit the highest-priced package (Replenishment Center)
   *
   * Called by section CTA buttons throughout the pricing page to automatically
   * select and submit the premium package (highest price). This provides a quick
   * conversion path without requiring users to manually choose a plan.
   *
   * @async
   * @returns {Promise<void>}
   *
   * @remarks
   * - Gets first item from paidBillingPackageList (pre-sorted by price descending)
   * - Includes safety check for empty package list
   * - Falls back to scrolling to pricing cards if no packages available
   * - Submits with 14-day trial by default
   * - Automatically applies first-month discount if eligible (via submitPackage)
   *
   * @example
   * ```vue
   * <v-btn @click="submitPremiumPackage">Get Started</v-btn>
   * ```
   *
   * @see submitPackage - Handles actual package submission with discount logic
   * @see paidBillingPackageList - Vuex getter that returns packages sorted by price
   */
  public async submitPremiumPackage() {
    // Check if paidBillingPackageList has items
    if (!this.paidBillingPackageList.items || this.paidBillingPackageList.items.length === 0) {
      console.warn('[OnboardPricing] No paid packages available');
      // Fallback: scroll to pricing cards so user can manually select
      this.scrollToPricingCards();
      return;
    }

    // Get first package (highest price) from the sorted list
    const premiumPackage = this.paidBillingPackageList.items[0];

    console.log('[OnboardPricing] Submitting premium package from CTA:', {
      name: premiumPackage.name,
      price: premiumPackage.price
    });
    await this.submitPackage(premiumPackage, 14);
  }
  
  // Helper method for feature toggle button
  public handleFeatureToggleClick(event: MouseEvent, item: BillingPackageModel) {
    // If shift key is pressed, show full comparison dialog
    if (event.shiftKey) {
      this.openFeatureComparison();
    } else {
      // Otherwise toggle view all features as usual
      this.viewAllFeatures = !this.viewAllFeatures;
    }
  }
  
  // Methods for feature comparison table
  public openFeatureComparison() {
    if (this.$refs.featureComparisonTable) {
      (this.$refs.featureComparisonTable as any).showFullComparisonDialog = true;
    }
  }
  
  // Add computed property to check if we should show feature comparison
  get shouldShowFeatureComparison(): boolean {
    // Count the number of billing packages with price > 0
    const paidPackagesCount = this.filteredBillingPackages.items.filter(item => item.price > 0).length;
    // Show feature comparison only if there are at least 2 paid plans
    return paidPackagesCount >= 2;
  }

  /**
   * Task 4.3: Two-check validation for monthly discount eligibility
   * Monthly discount requires BOTH:
   * 1. Timer active (< 30 minutes from installedAt)
   * 2. Valid discount code (API validation passed)
   */
  get hasValidMonthlyDiscount(): boolean {
    // Check 1: Timer must be active
    if (this.isOfferExpired) {
      return false;
    }

    // Check 2: Discount code must be valid (API returned discount)
    if (!this.currentDiscount) {
      return false;
    }

    // Check 3: Must be on monthly billing
    if (this.isYearly) {
      return false;
    }

    return true;
  }

  /**
   * Determine if a discount badge should be displayed for a specific package
   *
   * Controls visibility of the "FIRST MONTH SPECIAL" badge on pricing cards.
   * Only shows when all conditions are met to prevent showing badges without
   * actual discount application.
   *
   * @param {BillingPackageModel} packageItem - The package to check
   * @returns {boolean} True if discount badge should be visible
   *
   * @remarks
   * Validation checks (all must pass):
   * 1. Timer must be active (< 30 minutes from installation)
   * 2. API must have returned a valid discount code
   * 3. Must be on monthly billing (not yearly)
   * 4. Package code must match discount's target billingPackageCode
   *
   * @example
   * ```vue
   * <DiscountBadge
   *   v-if="shouldShowDiscountBadge(item)"
   *   type="FIRST_MONTH_SPECIAL"
   * />
   * ```
   *
   * @see hasValidMonthlyDiscount - Validates timer and discount availability
   * @see currentDiscount - Discount data from API including billingPackageCode
   */
  public shouldShowDiscountBadge(packageItem: BillingPackageModel): boolean {
    // Must have valid monthly discount
    if (!this.hasValidMonthlyDiscount) {
      return false;
    }

    // Must have billingPackageCode in discount
    if (!this.currentDiscount.billingPackageCode) {
      return false;
    }

    // Package code must match discount's target code
    const packageCode = (packageItem as any).code;
    return packageCode === this.currentDiscount.billingPackageCode;
  }

  /**
   * Scroll to pricing cards section with smooth animation
   *
   * Provides fallback navigation when automatic package submission fails
   * (e.g., no paid packages available). Ensures users can always access
   * the pricing cards even when CTA shortcuts don't work.
   *
   * @returns {void}
   *
   * @remarks
   * - Uses smooth scrolling for better UX
   * - Scrolls to 'start' position (top of pricing cards)
   * - Silently fails if .pricing-cards element not found
   * - Called by submitPremiumPackage when package list is empty
   *
   * @example
   * ```typescript
   * // Fallback when auto-submit fails
   * if (!packages.length) {
   *   this.scrollToPricingCards();
   * }
   * ```
   */
  public scrollToPricingCards() {
    const pricingSection = document.querySelector('.pricing-cards');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Get color code for countdown timer display
   *
   * Returns color intensity based on time remaining in offer. Creates visual
   * urgency as timer counts down. Wraps utility function for template usage.
   *
   * @returns {'green' | 'orange' | 'red'} Color code for countdown text
   *
   * @remarks
   * Color mapping:
   * - 'green': > 15 minutes remaining (normal state)
   * - 'orange': 5-15 minutes remaining (warning with pulse animation)
   * - 'red': < 5 minutes remaining (urgent with faster pulse)
   *
   * @example
   * ```vue
   * <StickyBottomBanner
   *   :countdown-color="getCountdownColor()"
   *   :countdown-text="countdownText"
   * />
   * ```
   *
   * @see getColorCode - Utility function in discountTimer.ts
   * @see StickyBottomBanner - Component that displays color-coded countdown
   */
  public getCountdownColor(): 'green' | 'orange' | 'red' {
    return getColorCode(this.remainingMs);
  }

  /**
   * Handle sticky banner CTA click
   *
   * Tracks banner interaction before submitting premium package.
   * Ensures analytics capture the conversion source.
   */
  public handleBannerCTAClick(): void {
    // Track banner click (non-blocking)
    try {
      if (this.pricingTracker && this.currentDiscount) {
        this.pricingTracker.trackDiscountBannerClicked({
          discountCode: this.currentDiscount.code,
          discountType: this.currentDiscount.type,
          discountValue: this.currentDiscount.rate || this.currentDiscount.discountAmount || 0,
          appliesTo: this.currentDiscount.billingPackageCode || 'all',
          isFirstMonthOnly: true,
        });
      }
    } catch (trackingError) {
      // Silently fail - never block conversion
      console.warn('[OnboardPricing] Banner click tracking failed:', trackingError);
    }

    // Proceed with package submission
    this.submitPremiumPackage();
  }

  /**
   * Handle ROI calculator interaction
   *
   * Tracks when user adjusts calculator values to see ROI projections.
   * Helps measure calculator engagement and its impact on conversions.
   *
   * @param calculationData - Current ROI calculation values
   */
  public handleROICalculatorInteraction(calculationData: any): void {
    // Track calculator interaction (non-blocking)
    try {
      if (this.pricingTracker) {
        this.pricingTracker.trackROICalculatorInteraction(calculationData);
      }
    } catch (trackingError) {
      // Silently fail - never block calculator UX
      console.warn('[OnboardPricing] ROI calculator tracking failed:', trackingError);
    }
  }
}
